'use client';

import {
  FloatingFocusManager,
  type FloatingFocusManagerProps,
  useMergeRefs,
} from '@floating-ui/react';
import { forwardRef, useContext } from 'react';
import type React from 'react';

import { css, cx } from '@/styled-system/css';

import { Slot, Slottable } from '../slot';
import { DropdownFloatingContext } from './context/dropdown-floating.context';
import { DropdownRootContext } from './context/dropdown-root.context';
import {
  dropdownContentRecipe,
  dropdownContentPositionerStyles,
} from './styles/dropdown-content.styles';
import type { DropdownContentPropsInterface } from './types/dropdown-content.types';

/**
 * @since 10.0.0
 */
export const DropdownContent = forwardRef<HTMLDivElement, DropdownContentPropsInterface>(
  (props: DropdownContentPropsInterface, forwardedRef) => {
    const { dropdown } = useContext(DropdownRootContext);
    const floating = useContext(DropdownFloatingContext);
    const {
      asChild = false,
      children,
      className,
      css: cssProp,
      focusManagerProps: userFocusManagerProps = {},
      style = {},
      ...rest
    } = props;

    const contentPositionerStyles = css(dropdownContentPositionerStyles);
    const contentStyles = css(
      dropdownContentRecipe.raw({ open: dropdown.transitionStatus === 'open' }),
      cssProp,
    );

    /*
     * Keep focus on the trigger when the user opens the panel, matching Menu's disclosure
     * pattern. `useClick` reports reason 'click' for pointer and keyboard (Enter/Space) alike,
     * so this covers both; users advance into the panel with Tab, which `preserveTabOrder` on
     * the portal makes reachable. Programmatic and `defaultOpen` opens never run through
     * `useClick`, so they keep FloatingFocusManager's default of focusing panel content.
     */
    const focusManagerProps: Partial<FloatingFocusManagerProps> = {
      order: ['content'],
      modal: false,
      ...(dropdown.openedByPointerRef.current && {
        initialFocus: dropdown.refs.domReference as React.MutableRefObject<HTMLElement | null>,
      }),
      ...userFocusManagerProps,
    };

    const mergedRefs = useMergeRefs([dropdown.refs.setFloating, forwardedRef]);

    // Don't use tabIndex from useInteractions mergeProps (default -1 on floating).
    // FloatingFocusManager sets tabindex on the floating focus element as needed (CDS-5822).
    const { tabIndex: _floatingTabIndex, ...floatingProps } = floating.getFloatingProps(rest);

    const Component = asChild ? Slot : 'div';

    return (
      <FloatingFocusManager context={floating.context} {...focusManagerProps}>
        <Component
          ref={mergedRefs}
          aria-labelledby={dropdown.labelId}
          aria-describedby={dropdown.descriptionId}
          data-c11n-version={__C11N_VERSION__}
          data-c11n-component="Dropdown.ContentPositioner"
          className={cx('c11n', 'c11n_dropdown__content-positioner', contentPositionerStyles)}
          style={{
            ...dropdown.floatingStyles,
            visibility: dropdown.middlewareData.hide?.referenceHidden ? 'hidden' : 'visible',
          }}
          {...floatingProps}
          id={dropdown.contentId}
        >
          <Slottable child={children}>
            {(slottable) => (
              <div
                data-c11n-version={__C11N_VERSION__}
                data-c11n-component="Dropdown.Content"
                className={cx('c11n', 'c11n_dropdown__content', contentStyles, className)}
                style={style}
              >
                {slottable}
              </div>
            )}
          </Slottable>
        </Component>
      </FloatingFocusManager>
    );
  },
);

DropdownContent.displayName = 'Dropdown.Content';
